/**
 * Өдрийн дэлгэрэнгүй — хуанлийн өдөр дээр дарахад нээгдэнэ.
 *
 * ЭРЭМБЭ (дээрээс доош): гол 4 тоо → борлуулалт (мөнгө, төлбөр,
 * салбар, баримт) → гутлын урсгал (шинэ гутал, шилжүүлэг, буцаалт,
 * актлалт, баталгаа). Хоосон хэсэг гарахгүй.
 *
 * Хөл дэх товчоор өмнөх / дараагийн өдөр рүү шилжинэ — сар солигдвол
 * хуанли ч дагана (`onPick`).
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  formatDate, formatMnt, formatQty, shiftDate, weekdayOfDateKey, type CalendarDayReport,
} from '@boots/domain';
import { api, type Envelope } from '../../lib/api';
import { Button, Modal } from '../ui';
import { StatCard } from '../stat-card';
import { EmptyState, ErrorState } from '../empty-state';
import { SkeletonStatGrid, SkeletonTable } from '../skeleton';
import { DaySales } from './day-sales';
import { DayStock } from './day-stock';

export function DayDetail({ date, today, onPick }: {
  date: string;
  today: string;
  onPick: (date: string | null) => void;
}) {
  const q = useQuery({
    queryKey: ['calendar-day', date],
    queryFn: () => api<Envelope<CalendarDayReport>>(`/reports/calendar/day?date=${date}`),
    placeholderData: keepPreviousData,
  });
  // Өмнөх өдрийн өгөгдлийг шинэ огнооны гарчиг дор харуулахгүй
  const r = q.data?.data.date === date ? q.data.data : undefined;

  return (
    <Modal
      wide
      title={`${formatDate(date)} · ${weekdayOfDateKey(date).label}`}
      subtitle={date === today ? 'Өнөөдөр — одоогийн байдлаар' : 'Өдрийн дэлгэрэнгүй'}
      onClose={() => onPick(null)}
      footer={
        <>
          <Button variant="ghost" className="mr-auto" onClick={() => onPick(shiftDate(date, -1))}>
            ‹ Өмнөх өдөр
          </Button>
          <Button variant="ghost" disabled={date >= today} onClick={() => onPick(shiftDate(date, 1))}>
            Дараагийн өдөр ›
          </Button>
          <Button onClick={() => onPick(null)}>Хаах</Button>
        </>
      }
    >
      {q.error ? (
        <ErrorState message={(q.error as Error).message} onRetry={() => void q.refetch()} />
      ) : !r ? (
        <>
          <SkeletonStatGrid />
          <SkeletonTable rows={4} cols={4} />
        </>
      ) : isEmpty(r) ? (
        <EmptyState
          title="Энэ өдөр бүртгэл алга"
          description="Борлуулалт, шинэ гутал, шилжүүлэг, буцаалт — аль нь ч бүртгэгдээгүй."
        />
      ) : (
        <>
          <DayKpis r={r} />
          <DaySales r={r} />
          <DayStock r={r} />
        </>
      )}
    </Modal>
  );
}

function isEmpty(r: CalendarDayReport): boolean {
  return (
    r.orders.length === 0 &&
    r.returns.length === 0 &&
    r.intake.registered === 0 &&
    r.intake.warehouseIn === 0 &&
    r.transfers.length === 0 &&
    r.writeOffs.length === 0 &&
    r.claims.length === 0
  );
}

function DayKpis({ r }: { r: CalendarDayReport }) {
  const s = r.sales;
  const trips = r.intake.trips.filter((t) => t.warehouseIn > 0).length;
  return (
    <div className="grid grid-cols-2 items-stretch gap-3 md:grid-cols-4">
      <StatCard
        icon="coins"
        label="Борлуулалт"
        value={formatMnt(s.salesMnt)}
        delta={{ text: `${formatQty(s.orders)} баримт` }}
      />
      <StatCard
        icon="receipt"
        label="Зарагдсан гутал"
        value={`${formatQty(s.units)} ш`}
        delta={
          s.returns.count > 0
            ? { text: `${formatQty(s.returns.count)} буцаалт`, good: false }
            : { text: r.stores.length > 0 ? `${formatQty(r.stores.length)} салбарт` : 'Борлуулалтгүй' }
        }
      />
      <StatCard
        icon="warehouse"
        label="Агуулахад орж ирсэн"
        value={`${formatQty(r.intake.warehouseIn)} ш`}
        {...(trips > 0 ? { delta: { text: `${formatQty(trips)} аяллаас` } } : {})}
      />
      <StatCard icon="box" label="Шинээр бүртгэгдсэн" value={`${formatQty(r.intake.registered)} ш`} />
    </div>
  );
}
